import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, useDraggable, useDroppable, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import { AlertTriangle, Bookmark, DollarSign, MoveRight, RotateCcw, Shuffle, Users, ZoomIn, ZoomOut } from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';
import type { CardData } from '../types/card';

const SHIFT_CAPACITY_MINUTES = 480;
const MIN_BLOCK_HEIGHT = 24;
const STACK_GAP = 2;
// Must match the rendered column header height (py-1.5 + leading-none text-[10px] + 1px border)
const COLUMN_HEADER_HEIGHT = 23;
// Must match footer height: border-t(1) + py-2(16) + text-[10px] line(~15) = 32px
const COLUMN_FOOTER_HEIGHT = 50;
// Shared header height between simulator title bar and panel sidebar header (in px)
const SHARED_HEADER_HEIGHT = 44;
export const UNASSIGNED_ROLE_ID = '__unassigned__';
const UNASSIGNED_ROLE_LABEL = 'Não atribuído';

export interface RoleColumn {
  id: string;
  name: string;
  roleId: string | null;
}

export interface ApplyPatch {
  cardId: string;
  roleId: string | null;
  roleName: string;
}

interface OrgRoleLite {
  id: string;
  name: string;
}

interface YamazumiSimulatorProps {
  cards: CardData[];
  assignments?: Record<string, string>;
  baseAssignments?: Record<string, string>;
  onAssignmentsChange?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  shiftMinutes?: number;
  demand?: number;
  fteByRole?: Record<string, number>;
  focusMode?: boolean;
  onToggleFocusMode?: () => void;
  showApplyButton?: boolean;
  onApplyToMap?: (patches: ApplyPatch[]) => void;
  saveBaselineToken?: number;
  /** Modo de visualização: planejado (padrão) ou real (dados da execução) */
  viewMode?: 'planned' | 'real';
  onViewModeChange?: (mode: 'planned' | 'real') => void;
  /** Carga real por cargo: roleId → YamazumiRealLoad (vindo do useYamazumiReal hook) */
  realLoadByRole?: Record<string, { totalActualMinutes: number; totalPlannedMinutes: number; efficiency: number | null; bottleneckCount: number }>;
  /** KPIs globais reais */
  realKpis?: { globalEfficiency: number | null; totalActualMinutes: number; deviationMinutes: number; leadTimeReal: number } | null;
  /** Controlled zoom — if provided, component becomes controlled and won't keep internal zoom state */
  zoom?: number;
  onZoomChange?: (z: number) => void;
  /** Controlled demand simulation % — if provided, component becomes controlled */
  demandaPercent?: number;
  onDemandaPercentChange?: (v: number) => void;
}

export function getCycleTimeMinutes(card: CardData): number {
  const tt = Number(card.tt) || 0;
  const tp = Number(card.tp) || 0;
  const lt = Number(card.lt) || 0;
  return tt || tp || lt || 0;
}

export function getRoleFromCard(card: CardData, roleLookup: Map<string, string>): RoleColumn {
  const primaryResource = card.resources?.[0];
  const resourceRoleId = primaryResource?.roleId ?? null;
  const fallbackName = card.responsavel || card.quem || (resourceRoleId ? roleLookup.get(resourceRoleId) : null);

  if (!resourceRoleId && (!fallbackName || fallbackName === UNASSIGNED_ROLE_LABEL)) {
    return { id: UNASSIGNED_ROLE_ID, name: UNASSIGNED_ROLE_LABEL, roleId: null };
  }

  const resolvedName = fallbackName ?? UNASSIGNED_ROLE_LABEL;

  return {
    id: resourceRoleId ?? resolvedName,
    name: resolvedName,
    roleId: resourceRoleId,
  };
}

export function buildYamazumiColumns(cards: CardData[], roles: OrgRoleLite[] = []): RoleColumn[] {
  const roleLookup = new Map<string, string>();
  for (const role of roles) roleLookup.set(role.id, role.name);

  const map = new Map<string, RoleColumn>();

  // SOURCE OF TRUTH: always include every org role as a column (even if empty)
  for (const role of roles) {
    if (role.id !== UNASSIGNED_ROLE_ID) {
      map.set(role.id, { id: role.id, name: role.name, roleId: role.id });
    }
  }

  // Cards may reference roles not yet in the org list — add them so nothing is lost
  for (const card of cards) {
    const role = getRoleFromCard(card, roleLookup);
    if (role.id !== UNASSIGNED_ROLE_ID && !map.has(role.id)) map.set(role.id, role);
  }

  // UNASSIGNED goes last (overflow bucket — only shown when it has cards)
  map.set(UNASSIGNED_ROLE_ID, { id: UNASSIGNED_ROLE_ID, name: UNASSIGNED_ROLE_LABEL, roleId: null });

  return Array.from(map.values());
}

export function buildInitialYamazumiAssignments(cards: CardData[], roles: OrgRoleLite[] = []): Record<string, string> {
  const roleLookup = new Map<string, string>();
  for (const role of roles) roleLookup.set(role.id, role.name);

  const next: Record<string, string> = {};
  for (const card of cards) {
    next[card.id] = getRoleFromCard(card, roleLookup).id;
  }
  return next;
}

export function buildInitialYamazumiFteByRole(cards: CardData[], roles: OrgRoleLite[] = []): Record<string, number> {
  const roleLookup = new Map<string, string>();
  for (const role of roles) roleLookup.set(role.id, role.name);

  const next: Record<string, number> = { [UNASSIGNED_ROLE_ID]: 1 };
  for (const role of roles) next[role.id] = 1;

  for (const card of cards) {
    const role = getRoleFromCard(card, roleLookup);
    const quantity = Math.max(Number(card.resources?.[0]?.quantity) || Number(card.pessoasSimultaneas) || 1, 1);
    next[role.id] = Math.max(next[role.id] ?? 1, quantity);
  }

  return next;
}

const KpiChip = ({ label, value, color }: { label: string; value: string; color: string }) => (
  <div className="flex flex-col items-center gap-0.5 min-w-[56px]">
        <span className="text-[9px] uppercase tracking-[0.1em] text-[var(--text-secondary)] leading-none">{label}</span>
        <span className={`font-mono text-[11px] font-bold leading-none ${color}`}>{value}</span>
  </div>
);

function DraggableActivityCard({ card, roleName, pxPerMin, shiftMinutes }: { card: CardData; roleName: string; pxPerMin: number; shiftMinutes: number }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    data: { cardId: card.id, roleName },
  });

  const minutes = Math.round(getCycleTimeMinutes(card));
  const heightPx = minutes * pxPerMin;
  const isTiny = heightPx < 40;
  const isSmall = heightPx < 70;

  // Color tier: how heavy is this card relative to a single-operator shift?
  const loadRatio = minutes / Math.max(shiftMinutes, 1);
  const cardStyle: React.CSSProperties = {
    opacity: isDragging ? 0.3 : 1,
    height: '100%',
    cursor: isDragging ? 'grabbing' : 'grab',
    transform: isDragging ? undefined : transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
    background: loadRatio >= 0.5
      ? 'linear-gradient(135deg, rgba(239,68,68,0.10), rgba(239,68,68,0.04))'
      : loadRatio >= 0.25
        ? 'linear-gradient(135deg, rgba(234,179,8,0.10), rgba(234,179,8,0.04))'
        : 'linear-gradient(135deg, rgba(59,130,246,0.10), rgba(59,130,246,0.04))',
    borderColor: loadRatio >= 0.5
      ? 'rgba(239,68,68,0.30)'
      : loadRatio >= 0.25
        ? 'rgba(234,179,8,0.30)'
        : 'rgba(59,130,246,0.25)',
  };

  const name = card.atividade || 'Atividade sem nome';

  return (
    <button
      ref={setNodeRef}
      style={cardStyle}
      {...listeners}
      {...attributes}
      title={`${name} • ${minutes} min • ${roleName}`}
      type="button"
      className="group relative flex h-full w-full overflow-visible rounded-[6px] border text-left shadow-[0_2px_6px_rgba(0,0,0,0.12)] transition-all duration-[120ms] hover:shadow-[0_6px_16px_rgba(0,0,0,0.22)] hover:scale-[1.01] hover:brightness-105 active:scale-[0.98]"
    >
      {!isDragging && (
        <div className="pointer-events-none absolute bottom-full left-0 z-[9999] mb-1 min-w-[160px] max-w-[220px] rounded-md border border-[var(--border-subtle)] bg-[var(--bg-app)] px-2 py-1 text-[10px] text-[var(--text-primary)] opacity-0 shadow-xl transition-opacity delay-200 duration-150 group-hover:opacity-100">
          <div className="font-semibold">{name}</div>
          <div>{minutes} min</div>
          <div className="opacity-70">{roleName}</div>
        </div>
      )}

      {isTiny ? (
        /* ── TINY: single row — time + name ── */
        <div className="flex w-full items-center justify-between gap-1 px-1.5" style={{ minWidth: 0 }}>
          <span className="shrink-0 font-mono text-[9px] leading-none text-[var(--text-secondary)] opacity-75">{minutes}m</span>
          <span className="truncate text-[9px] font-semibold leading-none text-[var(--text-primary)]">{name}</span>
        </div>
      ) : isSmall ? (
        /* ── SMALL: name + time, no role ── */
        <div className="flex w-full flex-col justify-center gap-0.5 px-2 py-1" title={roleName}>
          <p className="truncate text-[10px] font-semibold leading-tight text-[var(--text-primary)]">{name}</p>
          <span className="font-mono text-[9px] leading-none text-[var(--text-secondary)] opacity-75">{minutes}m</span>
        </div>
      ) : (
        /* ── NORMAL: name + time + role ── */
        <div className="flex w-full flex-col justify-between px-2.5 py-1.5">
          <p className="truncate text-[11px] font-semibold leading-tight text-[var(--text-primary)]">{name}</p>
          <div className="mt-1 flex items-center justify-between gap-2 text-[11px] leading-none text-[var(--text-secondary)] opacity-75">
            <span className="font-mono">{minutes}m</span>
            <span className="truncate text-right">{roleName}</span>
          </div>
        </div>
      )}
    </button>
  );
}

function DroppableColumn({
  column,
  cards,
  totalLoad,
  capacity,
  fteCount,
  pxPerMin,
  canvasHeight,
  scrollTop,
  viewportPxHeight,
  taktMinutes,
  activeDragCardId,
  realLoad,
  viewMode,
  isSimulated,
  stableLoad,
  isDragging,
}: {
  column: RoleColumn;
  cards: CardData[];
  totalLoad: number;
  capacity: number;
  fteCount: number;
  pxPerMin: number;
  canvasHeight: number;
  scrollTop: number;
  viewportPxHeight: number;
  taktMinutes?: number;
  activeDragCardId: string | null;
  realLoad?: { totalActualMinutes: number; totalPlannedMinutes: number; efficiency: number | null; bottleneckCount: number } | null;
  viewMode?: 'planned' | 'real';
  isSimulated?: boolean;
  stableLoad?: number;
  isDragging?: boolean;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: column.id });

  const occupationPercent = Math.round((totalLoad / Math.max(capacity, 1)) * 100);
  const isOverloaded = occupationPercent > 100;
  const isSimulatedOverloaded = !isOverloaded && isSimulated && (totalLoad > capacity);

  // Drag impact: compare preview load vs stable (committed) load
  const dragImpact: 'improved' | 'worsened' | null = (() => {
    if (!isDragging || stableLoad === undefined) return null;
    const diff = totalLoad - stableLoad;
    if (Math.abs(diff) < 0.01) return null;
    return diff < 0 ? 'improved' : 'worsened';
  })();
  const isAttention = occupationPercent >= 85 && occupationPercent <= 100;
  const remaining = Math.max(capacity - totalLoad, 0);

  const stackedLayouts = useMemo(() => {
    let acc = 0;
    return cards.map((card) => {
      const h = Math.max(MIN_BLOCK_HEIGHT, getCycleTimeMinutes(card) * pxPerMin);
      const layout = { card, height: h, bottom: acc };
      acc += h + STACK_GAP;
      return layout;
    });
  }, [cards, pxPerMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Virtualisation window in plot bottom-coords
  const VIRT_BUFFER = 300;
  const plotScrollTop = Math.max(0, scrollTop - COLUMN_HEADER_HEIGHT);
  const plotScrollBottom = Math.min(canvasHeight, scrollTop + viewportPxHeight - COLUMN_HEADER_HEIGHT);
  const visibleBottomMax = canvasHeight - plotScrollTop + VIRT_BUFFER;
  const visibleBottomMin = canvasHeight - plotScrollBottom - VIRT_BUFFER;

  const visibleLayouts = useMemo(() => {
    if (activeDragCardId !== null) return stackedLayouts;
    return stackedLayouts.filter(
      ({ bottom, height }) => bottom + height >= visibleBottomMin && bottom <= visibleBottomMax,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackedLayouts, visibleBottomMax, visibleBottomMin, activeDragCardId]);

  const capacityLineBottom = Math.min(capacity * pxPerMin, canvasHeight);
  const overflowPx = Math.max(0, totalLoad * pxPerMin - capacityLineBottom);

  const containerBorder = isOverloaded ? 'border-[var(--risk-critical)]'
    : isSimulatedOverloaded ? 'border-amber-500'
    : dragImpact === 'improved' ? 'border-emerald-500'
    : dragImpact === 'worsened' ? 'border-red-400'
    : isAttention ? 'border-[var(--risk-alert)]'
    : isOver ? 'border-[var(--brand-primary)]'
    : 'border-[var(--border-default)]';
  const headerTone = isOverloaded
    ? 'border-[var(--risk-critical)] bg-red-500/10 text-[var(--risk-critical)]'
    : isSimulatedOverloaded
      ? 'border-amber-500 bg-amber-500/15 text-amber-400'
      : isAttention
        ? 'border-[var(--risk-alert)] bg-amber-500/10 text-[var(--risk-alert)]'
        : 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)]';
  const footerTone = isOverloaded ? 'text-[var(--risk-critical)]' : isAttention ? 'text-[var(--risk-alert)]' : 'text-[var(--text-secondary)]';

  return (
    <div
      ref={setNodeRef}
      className={`flex h-full min-w-[240px] max-w-[260px] flex-col overflow-hidden rounded-[6px] border transition-all duration-150 ${containerBorder} ${
        dragImpact === 'improved' ? 'shadow-[0_0_0_2px_rgba(52,211,153,0.35)]'
        : dragImpact === 'worsened' ? 'shadow-[0_0_0_2px_rgba(248,113,113,0.35)]'
        : isOver ? 'bg-[var(--brand-primary-soft)] shadow-[0_0_0_2px_var(--brand-primary)]'
        : 'bg-[var(--bg-elevated)]'
      }`}
    >
      {/* Column header — fixed height, never grows */}
      <div
        className={`shrink-0 border-b px-2.5 py-1.5 ${headerTone}`}
        style={{ height: `${COLUMN_HEADER_HEIGHT}px`, minHeight: `${COLUMN_HEADER_HEIGHT}px` }}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-[10px] font-bold uppercase tracking-[0.04em] leading-none">
            {column.name} <span className="font-mono opacity-70">[{fteCount}x]</span>
          </p>
          <p className="shrink-0 font-mono text-[10px] leading-none">
            {Math.round(totalLoad)}<span className="opacity-60">/{capacity}</span>
            {isOverloaded && <span className="ml-1 rounded bg-red-500/20 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-[var(--risk-critical)]">Gargalo</span>}
            {isSimulatedOverloaded && <span className="ml-1 rounded bg-amber-500/20 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-amber-400">SIM</span>}
            {dragImpact === 'improved' && (
              <span className="ml-1 rounded bg-emerald-500/20 px-1 py-px text-[8px] font-bold leading-none text-emerald-400">
                −{Math.round(Math.abs(totalLoad - (stableLoad ?? totalLoad)))}m
              </span>
            )}
            {dragImpact === 'worsened' && (
              <span className="ml-1 rounded bg-red-500/20 px-1 py-px text-[8px] font-bold leading-none text-red-400">
                +{Math.round(Math.abs(totalLoad - (stableLoad ?? totalLoad)))}m
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Plot canvas — explicit height so bottom:Xpx cards have a concrete reference; overflow:hidden clips excess */}
      <div className="relative shrink-0 overflow-hidden" style={{ height: `${canvasHeight}px` }}>
        {/* Drop highlight overlay */}
        {isOver && (
          <div className="pointer-events-none absolute inset-0 z-30 rounded-b-md border-2 border-dashed border-blue-400/60 bg-blue-500/5" />
        )}
        {/* Overflow zone */}
        {isOverloaded && (
          <div
            className="pointer-events-none absolute left-2 right-2 rounded-sm bg-red-500/10"
            style={{ bottom: `${capacityLineBottom}px`, height: `${overflowPx}px` }}
          />
        )}
        {/* Capacity line */}
        <div
          className={`pointer-events-none absolute left-2 right-2 border-t border-dashed opacity-50 ${isOverloaded ? 'border-[var(--risk-critical)]' : 'border-[var(--text-secondary)]'}`}
          style={{ bottom: `${capacityLineBottom}px` }}
        />
        <span
          className="pointer-events-none absolute right-2 rounded bg-[var(--bg-app)] px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.12em] text-[var(--text-secondary)]"
          style={{ bottom: `${capacityLineBottom + 2}px` }}
        >Cap</span>
        {/* Takt line */}
        {taktMinutes != null && taktMinutes > 0 && (
          <>
            <div
              className="pointer-events-none absolute left-0 right-0 z-20 border-t-2 border-dashed border-[var(--risk-critical)] opacity-70"
              style={{ bottom: `${Math.min(taktMinutes * pxPerMin, canvasHeight - 2)}px` }}
            />
            <span
              className="pointer-events-none absolute left-1 z-20 rounded bg-[var(--bg-surface)] px-1 py-0.5 text-[8px] font-mono text-[var(--risk-critical)]"
              style={{ bottom: `${Math.min(taktMinutes * pxPerMin + 2, canvasHeight - 14)}px` }}
            >TT</span>
          </>
        )}
        {/* Empty state */}
        {cards.length === 0 && !isOver && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-[11px] font-semibold text-[var(--text-primary)]">Capacidade disponível</span>
            <span className="mt-1 text-[10px] text-[var(--text-secondary)]">0% ocupado</span>
          </div>
        )}
        {isOver && cards.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[11px] font-semibold text-blue-300">Soltar aqui</span>
          </div>
        )}
        {/* Virtualised activity cards */}
        {visibleLayouts.map(({ card, height, bottom }) => (
          <div
            key={card.id}
            className="absolute left-0 right-0 z-10 px-1.5"
            style={{
              bottom: `${Math.max(0, Math.min(bottom, canvasHeight - height))}px`,
              height: `${height}px`,
              paddingBottom: '2px',
            }}
          >
            <DraggableActivityCard card={card} roleName={column.name} pxPerMin={pxPerMin} shiftMinutes={capacity / Math.max(fteCount, 1)} />
          </div>
        ))}
        {/* Barra de carga real (modo Real) */}
        {viewMode === 'real' && realLoad && realLoad.totalActualMinutes > 0 && (
          <>
            {/* Barra sólida mostrando carga real */}
            <div
              className={`pointer-events-none absolute left-0 z-20 w-[6px] rounded-t-sm opacity-80 ${
                realLoad.efficiency == null ? 'bg-slate-400'
                : realLoad.efficiency >= 0.95 ? 'bg-emerald-400'
                : realLoad.efficiency >= 0.80 ? 'bg-amber-400'
                : 'bg-red-400'
              }`}
              style={{
                bottom: 0,
                height: `${Math.min(realLoad.totalActualMinutes * pxPerMin, canvasHeight)}px`,
              }}
              title={`Real: ${Math.round(realLoad.totalActualMinutes)}min | Efic: ${realLoad.efficiency != null ? Math.round(realLoad.efficiency * 100) + '%' : '—'}`}
            />
            {/* Linha horizontal marcando o nível planejado (para comparação visual) */}
            {realLoad.totalPlannedMinutes > 0 && (
              <div
                className="pointer-events-none absolute left-0 z-20 w-[6px] border-t-2 border-[var(--text-primary)] opacity-50"
                style={{ bottom: `${Math.min(realLoad.totalPlannedMinutes * pxPerMin, canvasHeight)}px` }}
              />
            )}
          </>
        )}
      </div>

      {/* Footer — fixed height, never grows */}
      <div
        className={`shrink-0 flex flex-col justify-center gap-0.5 border-t border-white/[0.06] px-3 text-[10px] ${footerTone}`}
        style={{ height: `${COLUMN_FOOTER_HEIGHT}px`, minHeight: `${COLUMN_FOOTER_HEIGHT}px` }}
      >
        {/* Row 1: occupancy status */}
        <div className="opacity-70">
          {isOverloaded ? (
            <span className="inline-flex items-center gap-1 font-semibold">
              <AlertTriangle size={12} /> Sobrecarga ({occupationPercent}%)
            </span>
          ) : isAttention ? (
            <span className="font-semibold">Atenção ({occupationPercent}%)</span>
          ) : (
            <span>{remaining} min livres</span>
          )}
        </div>
        {/* Row 2: headcount analysis */}
        {(() => {
          const shiftMin = capacity / Math.max(fteCount, 1);
          const necessario = calcularHeadcount(totalLoad, shiftMin);
          const delta = necessario - fteCount;
          const tone =
            delta > 1 ? 'text-[var(--risk-critical)]' :
            delta === 1 ? 'text-amber-400' :
            'text-emerald-400';
          const badge =
            delta > 1 ? 'bg-red-500/20 text-[var(--risk-critical)]' :
            delta === 1 ? 'bg-amber-500/20 text-amber-400' :
            'bg-emerald-500/15 text-emerald-400';
          return (
            <div className={`flex items-center gap-1.5 font-mono text-[9px] ${tone}`}>
              <span className="opacity-60">Atual <strong>{fteCount}</strong></span>
              <span className="opacity-40">·</span>
              <span className="opacity-60">Nec. <strong>{necessario}</strong></span>
              {delta !== 0 && (
                <span className={`rounded px-1 py-px text-[8px] font-bold leading-none ${badge}`}>
                  {delta > 0 ? `+${delta}` : delta}
                </span>
              )}
              {delta <= 0 && (
                <span className="rounded bg-emerald-500/15 px-1 py-px text-[8px] font-bold leading-none text-emerald-400">ok</span>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/**
 * Mock hourly cost by roleId (BRL). Used as fallback until financial integration.
 * Key: roleId (UUID or display name). Fallback: DEFAULT_COST_PER_HOUR.
 */
export const DEFAULT_COST_PER_HOUR = 75; // R$/h — generic operator baseline
export const ROLE_COST_OVERRIDES: Record<string, number> = {
  // example overrides — replace with real role UUIDs when available
  engenheiro: 150,
  lider: 120,
  analista: 100,
  operador: 60,
};

/**
 * Returns the hourly cost for a given roleId.
 * Looks up ROLE_COST_OVERRIDES by exact match or partial lowercase match.
 */
export function getCustoHora(roleId: string | null): number {
  if (!roleId) return DEFAULT_COST_PER_HOUR;
  if (ROLE_COST_OVERRIDES[roleId] != null) return ROLE_COST_OVERRIDES[roleId];
  const lower = roleId.toLowerCase();
  for (const [key, val] of Object.entries(ROLE_COST_OVERRIDES)) {
    if (lower.includes(key)) return val;
  }
  return DEFAULT_COST_PER_HOUR;
}

/**
 * Calculates operational cost for a given load (minutes) and role.
 * custo = (tempo_min / 60) * custoHora
 */
export function calcularCusto(
  cargaMinutos: number,
  roleId: string | null,
  fteCount = 1,
): number {
  const custoHora = getCustoHora(roleId);
  return (cargaMinutos / 60) * custoHora * Math.max(fteCount, 1);
}

/**
 * Returns true when a column's load exceeds its capacity (bottleneck condition).
 */
export function detectarGargalo(totalLoad: number, capacity: number): boolean {
  return totalLoad > capacity;
}

/**
 * Calculates the estimated process lead time (minutes).
 * Lead time = max load among all role columns (bottleneck-based).
 */
export function calcularLeadTime(totalsByColumn: Record<string, number>): number {
  const values = Object.values(totalsByColumn);
  if (values.length === 0) return 0;
  return Math.max(...values);
}

/**
 * Calculates the minimum number of people (headcount) needed to handle a given total load
 * within a single shift. Pure function — no side effects.
 */
export function calcularHeadcount(cargaTotal: number, tempoTurno: number): number {
  if (tempoTurno <= 0 || cargaTotal <= 0) return 0;
  return Math.ceil(cargaTotal / tempoTurno);
}

/**
 * Returns a new map with each column's load multiplied by (1 + percentual/100).
 * Does NOT mutate source data.
 */
export function simularDemanda(
  totalsByColumn: Record<string, number>,
  percentual: number,
): Record<string, number> {
  const fator = 1 + Math.max(0, percentual) / 100;
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(totalsByColumn)) {
    result[key] = value * fator;
  }
  return result;
}


export const YamazumiSimulator: React.FC<YamazumiSimulatorProps> = ({
  cards,
  assignments,
  baseAssignments,
  onAssignmentsChange,
  shiftMinutes = SHIFT_CAPACITY_MINUTES,
  demand = 0,
  fteByRole = {},
  focusMode = false,
  onToggleFocusMode,
  showApplyButton = true,
  onApplyToMap,
  saveBaselineToken,
  viewMode = 'planned',
  onViewModeChange,
  realLoadByRole = {},
  realKpis = null,
  zoom: zoomProp,
  onZoomChange,
  demandaPercent: demandaPercentProp,
  onDemandaPercentChange,
}) => {
  const { organization } = useProjectStore();

  const activityCards = useMemo(
    () => cards.filter(
      (card) =>
        card.tipo === 'atividade' &&
        card.status === 'ativo' &&
        card.tipo !== 'card_inicial' &&
        card.tipo !== 'card_final' &&
        card.tipo !== 'decisao' &&
        getCycleTimeMinutes(card) > 0
    ),
    [cards]
  );

  const roleLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const role of organization?.roles ?? []) {
      map.set(role.id, role.name);
    }
    return map;
  }, [organization?.roles]);

  const columns = useMemo(
    () => buildYamazumiColumns(activityCards, organization?.roles ?? []),
    [activityCards, organization?.roles]
  );

  // Guard: ensure all org roles are always represented — protects against edge-case state drift
  const columnIds = useMemo(() => new Set(columns.map((c) => c.id)), [columns]);
  const missingRoles = useMemo(
    () => (organization?.roles ?? []).filter((r: OrgRoleLite) => !columnIds.has(r.id)),
    [organization?.roles, columnIds]
  );
  // If any role column is missing, the columns memo above will self-correct on the next render
  // because buildYamazumiColumns now treats org roles as source of truth.
  // This ref ensures we never silently lose a column in production.
  const _missingRolesGuard = missingRoles.length; // referenced to avoid lint warning
  void _missingRolesGuard;

  const buildAssignments = useCallback(
    () => buildInitialYamazumiAssignments(activityCards, organization?.roles ?? []),
    [activityCards, organization?.roles]
  );

  const assignmentSignature = useMemo(
    () => JSON.stringify(activityCards.map((card) => [card.id, getRoleFromCard(card, roleLookup).id])),
    [activityCards, roleLookup]
  );

  const [localAssignments, setLocalAssignments] = useState<Record<string, string>>({});
  const [localBaseAssignments, setLocalBaseAssignments] = useState<Record<string, string>>({});
  const [activeDragCardId, setActiveDragCardId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [isDragOverValid, setIsDragOverValid] = useState(false);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [zoomInternal, setZoomInternal] = useState(1.0);
  const zoom = zoomProp ?? zoomInternal;
  const setZoom = useCallback((v: number) => { setZoomInternal(v); onZoomChange?.(v); }, [onZoomChange]);
  const [savedBaseline, setSavedBaseline] = useState<{ assignments: Record<string, string>; occupation: number; leadTimeMinutes: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [demandaPercentInternal, setDemandaPercentInternal] = useState(0);
  const demandaPercent = demandaPercentProp ?? demandaPercentInternal;
  const setDemandaPercent = useCallback((v: number) => { setDemandaPercentInternal(v); onDemandaPercentChange?.(v); }, [onDemandaPercentChange]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const next = buildAssignments();
    if (assignments === undefined) setLocalAssignments(next);
    setLocalBaseAssignments(next);
  }, [assignmentSignature, buildAssignments, assignments]);

  // Track actual rendered container height — the only reliable way to guarantee no scroll at zoom=1
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerHeight(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const activeAssignments = assignments ?? localAssignments;
  const setActiveAssignments = onAssignmentsChange ?? setLocalAssignments;
  const baselineAssignments = baseAssignments ?? localBaseAssignments;

  const groupedCards = useMemo(() => {
    const groups: Record<string, CardData[]> = {};
    for (const column of columns) groups[column.id] = [];
    for (const card of activityCards) {
      const columnId = activeAssignments[card.id] ?? UNASSIGNED_ROLE_ID;
      if (!groups[columnId]) groups[columnId] = [];
      groups[columnId].push(card);
    }
    return groups;
  }, [activityCards, activeAssignments, columns]);

  const totalsByColumn = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const column of columns) {
      totals[column.id] = (groupedCards[column.id] ?? []).reduce((acc, card) => acc + getCycleTimeMinutes(card), 0);
    }
    return totals;
  }, [columns, groupedCards]);

  // Preview totals: apply drag hover to show load shift before drop commits
  const previewTotalsByColumn = useMemo(() => {
    if (!activeDragCardId || !dragOverColumnId) return totalsByColumn;
    const card = activityCards.find((c) => c.id === activeDragCardId);
    if (!card) return totalsByColumn;
    const sourceColId = activeAssignments[activeDragCardId] ?? UNASSIGNED_ROLE_ID;
    if (sourceColId === dragOverColumnId) return totalsByColumn;
    const load = getCycleTimeMinutes(card);
    return {
      ...totalsByColumn,
      [sourceColId]: Math.max(0, (totalsByColumn[sourceColId] ?? 0) - load),
      [dragOverColumnId]: (totalsByColumn[dragOverColumnId] ?? 0) + load,
    };
  }, [activeDragCardId, dragOverColumnId, totalsByColumn, activityCards, activeAssignments]);

  const simulatedTotalsByColumn = useMemo(
    () => demandaPercent > 0 ? simularDemanda(previewTotalsByColumn, demandaPercent) : previewTotalsByColumn,
    [previewTotalsByColumn, demandaPercent],
  );

  const isSimulating = demandaPercent > 0;

  const capacitiesByColumn = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const column of columns) {
      totals[column.id] = Math.max(1, Number(fteByRole[column.id] ?? 1)) * shiftMinutes;
    }
    return totals;
  }, [columns, fteByRole, shiftMinutes]);

  // plotHeight = exact pixel height available for the chart area (header + footer subtracted)
  // Derived from ResizeObserver → guaranteed to match the real DOM, so zoom=1 = zero scroll
  const plotHeight = useMemo(
    () => Math.max(100, (containerHeight || 600) - SHARED_HEADER_HEIGHT - COLUMN_HEADER_HEIGHT - COLUMN_FOOTER_HEIGHT),
    [containerHeight]
  );

  const basePxPerMin = plotHeight / Math.max(shiftMinutes, 1);
  const pxPerMin = basePxPerMin * zoom;

  const maxCapacity = useMemo(
    () => Math.max(...columns.map((c) => capacitiesByColumn[c.id] ?? shiftMinutes), shiftMinutes),
    [columns, capacitiesByColumn, shiftMinutes]
  );

  // zoom=1 → canvasHeight = plotHeight (exact fit, overflow:hidden = zero scroll)
  // zoom>1 → canvas expands → overflow:auto activates scroll
  const canvasHeight = zoom === 1
    ? plotHeight
    : Math.max(plotHeight, maxCapacity * pxPerMin);

  const overallLoad = useMemo(
    () => activityCards.reduce((acc, card) => acc + getCycleTimeMinutes(card), 0),
    [activityCards]
  );
  const simulatedOverallLoad = overallLoad * (1 + demandaPercent / 100);
  const overallCapacity = useMemo(
    () => columns.reduce((acc, column) => acc + (capacitiesByColumn[column.id] ?? 0), 0),
    [columns, capacitiesByColumn]
  );
  const overallOccupation = useMemo(
    () => Math.round((simulatedOverallLoad / Math.max(overallCapacity, 1)) * 100),
    [simulatedOverallLoad, overallCapacity]
  );
  const bottleneckCount = useMemo(
    () => columns.filter((column) => (simulatedTotalsByColumn[column.id] ?? 0) > (capacitiesByColumn[column.id] ?? 0)).length,
    [columns, simulatedTotalsByColumn, capacitiesByColumn]
  );
  const taktMinutes = useMemo(
    () => (demand > 0 ? shiftMinutes / demand : null),
    [demand, shiftMinutes]
  );
  const leadTimeMinutes = useMemo(
    () => calcularLeadTime(simulatedTotalsByColumn),
    [simulatedTotalsByColumn]
  );

  const custoTotal = useMemo(() => {
    let total = 0;
    for (const column of columns) {
      const load = simulatedTotalsByColumn[column.id] ?? 0;
      const fte = Math.max(1, Number(fteByRole[column.id] ?? 1));
      total += calcularCusto(load, column.roleId, fte);
    }
    return total;
  }, [columns, simulatedTotalsByColumn, fteByRole]);

  const custoMedioPorTarefa = activityCards.length > 0
    ? custoTotal / activityCards.length
    : 0;
  const yAxisTicks = useMemo(
    () => (shiftMinutes === 480 ? [0, 120, 240, 360, 480] : [0, Math.round(shiftMinutes * 0.25), Math.round(shiftMinutes * 0.5), Math.round(shiftMinutes * 0.75), shiftMinutes]),
    [shiftMinutes]
  );
  const activeDragCard = useMemo(
    () => activityCards.find((card) => card.id === activeDragCardId) ?? null,
    [activityCards, activeDragCardId]
  );

  const activeDragCardHeight = useMemo(() => {
    if (!activeDragCard) return 0;
    return Math.max(MIN_BLOCK_HEIGHT, getCycleTimeMinutes(activeDragCard) * pxPerMin);
  }, [activeDragCard, pxPerMin]);

  const changedCount = useMemo(
    () => activityCards.filter((card) => (activeAssignments[card.id] ?? UNASSIGNED_ROLE_ID) !== (baselineAssignments[card.id] ?? UNASSIGNED_ROLE_ID)).length,
    [activityCards, activeAssignments, baselineAssignments]
  );

  // Preserve center-point when zooming; scroll to bottom when first entering zoom>1
  const handleZoomChange = useCallback((newZoom: number) => {
    const clamped = Math.max(0.5, Math.min(3, +newZoom.toFixed(2)));
    const base = plotHeight / Math.max(shiftMinutes, 1);
    const oldPxPerMin = base * zoom;
    const newPxPerMin = base * clamped;
    const st = scrollRef.current?.scrollTop ?? scrollTop;
    const oldCanvas = zoom === 1 ? plotHeight : Math.max(plotHeight, maxCapacity * oldPxPerMin);
    const newCanvas = clamped === 1 ? plotHeight : Math.max(plotHeight, maxCapacity * newPxPerMin);
    // Pin the time under the viewport center
    const centerBottom = oldCanvas - st - plotHeight / 2;
    const centerTime = centerBottom / Math.max(oldPxPerMin, 0.001);
    const newScrollTop = zoom === 1
      ? Math.max(0, newCanvas - plotHeight)            // entering zoom>1: show bottom (time=0)
      : Math.max(0, newCanvas - centerTime * newPxPerMin - plotHeight / 2);
    setZoom(clamped);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = newScrollTop;
    });
  }, [zoom, scrollTop, plotHeight, shiftMinutes, maxCapacity]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragCardId(String(event.active.id));
    setIsDragOverValid(false);
    setDragOverColumnId(null);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over ? String(event.over.id) : null;
    const valid = !!overId && columns.some((c) => c.id === overId);
    setIsDragOverValid(valid);
    setDragOverColumnId(valid ? overId : null);
  }, [columns]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const cardId = String(event.active.id);
    const nextColumnId = event.over ? String(event.over.id) : null;
    setActiveDragCardId(null);
    setDragOverColumnId(null);
    setIsDragOverValid(false);
    if (!nextColumnId || !columns.some((c) => c.id === nextColumnId)) return;
    setActiveAssignments((prev) => ({ ...prev, [cardId]: nextColumnId }));
  }, [setActiveAssignments, columns]);

  const handleDragCancel = useCallback(() => {
    setActiveDragCardId(null);
    setDragOverColumnId(null);
    setIsDragOverValid(false);
  }, []);

  useEffect(() => {
    if (saveBaselineToken === undefined) return;
    setSavedBaseline({ assignments: { ...activeAssignments }, occupation: overallOccupation, leadTimeMinutes });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveBaselineToken]);

  const handleAutoBalance = useCallback(() => {
    const assignableCols = columns.filter((c) => c.id !== UNASSIGNED_ROLE_ID);
    if (assignableCols.length === 0) return;
    const sorted = [...activityCards].sort((a, b) => getCycleTimeMinutes(b) - getCycleTimeMinutes(a));
    const loads: Record<string, number> = {};
    for (const col of assignableCols) loads[col.id] = 0;
    const next: Record<string, string> = {};
    for (const card of sorted) {
      const minCol = assignableCols.reduce((a, b) => (loads[a.id] ?? 0) <= (loads[b.id] ?? 0) ? a : b);
      next[card.id] = minCol.id;
      loads[minCol.id] = (loads[minCol.id] ?? 0) + getCycleTimeMinutes(card);
    }
    setActiveAssignments(next);
  }, [activityCards, columns, setActiveAssignments]);

  const handleSaveBaseline = useCallback(() => {
    setSavedBaseline({ assignments: { ...activeAssignments }, occupation: overallOccupation, leadTimeMinutes });
  }, [activeAssignments, overallOccupation, leadTimeMinutes]);

  const handleApply = useCallback(() => {
    if (!onApplyToMap) return;
    const patches: ApplyPatch[] = activityCards
      .filter((card) => (activeAssignments[card.id] ?? UNASSIGNED_ROLE_ID) !== (baselineAssignments[card.id] ?? UNASSIGNED_ROLE_ID))
      .map((card) => {
        const targetColumn = columns.find((column) => column.id === (activeAssignments[card.id] ?? UNASSIGNED_ROLE_ID));
        return {
          cardId: card.id,
          roleId: targetColumn?.roleId ?? null,
          roleName: targetColumn?.name ?? UNASSIGNED_ROLE_LABEL,
        };
      });

    if (patches.length === 0) return;
    onApplyToMap(patches);
    setLocalBaseAssignments(activeAssignments);
  }, [activityCards, activeAssignments, baselineAssignments, columns, onApplyToMap]);

  if (activityCards.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--bg-app)]"
      style={{ height: focusMode ? 'calc(100vh - var(--shell-header-height, 32px) - var(--shell-statusbar-height, 22px))' : '100%' }}
    >
      {/* ── Header — context only ── */}
      <div className="border-b border-[var(--border-default)] px-4" style={{ height: `${SHARED_HEADER_HEIGHT}px`, minHeight: `${SHARED_HEADER_HEIGHT}px`, boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}>
        <div className="flex flex-1 items-center justify-between gap-3 min-w-0">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--text-primary)] leading-none">
              Simulador Yamazumi
            </p>
            <p className="mt-1 text-[10px] leading-none text-[var(--text-secondary)]">
              {columns.filter((c) => c.id !== UNASSIGNED_ROLE_ID).length} cargos
              {' · '}{activityCards.length} atividades
            </p>
          </div>
          {/* Toggle Planejado | Real */}
          {onViewModeChange && (
            <div className="inline-flex items-center overflow-hidden rounded border border-[var(--border-default)] bg-[var(--bg-surface)]">
              <button
                type="button"
                onClick={() => onViewModeChange('planned')}
                className={`px-2 py-[2px] text-[9px] font-semibold uppercase tracking-wide transition-colors ${viewMode === 'planned' ? 'bg-[var(--nav-active-bar)] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--button-hover)]'}`}
              >
                Planejado
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange('real')}
                className={`px-2 py-[2px] text-[9px] font-semibold uppercase tracking-wide transition-colors ${viewMode === 'real' ? 'bg-[var(--nav-active-bar)] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--button-hover)]'}`}
              >
                Real
              </button>
            </div>
          )}
        </div>
      </div>

      <DndContext onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        {/* Canvas area: dynamic overflow — hidden at zoom=1 (no scroll), auto at zoom>1 */}
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 px-3 py-0.5 bg-[var(--bg-app)]"
          style={{ overflow: zoom === 1 ? 'hidden' : 'auto' }}
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        >
          <div
            className="flex items-start gap-3"
            style={{ minWidth: 'max-content', height: zoom === 1 ? '100%' : undefined }}
          >
            {/* Y-axis: explicit height=canvasHeight so tick positions match column plot exactly */}
            <div className="sticky left-0 z-10 mr-0 flex shrink-0 flex-col bg-[var(--bg-app)]">
              <div style={{ height: `${COLUMN_HEADER_HEIGHT}px`, minHeight: `${COLUMN_HEADER_HEIGHT}px` }} />
              <div
                className="relative w-10 shrink-0 border-r border-[var(--border-default)] pr-2 text-right text-[10px] font-mono text-[var(--text-secondary)]"
                style={{ height: `${canvasHeight}px` }}
              >
                {yAxisTicks.map((tick) => (
                  <span
                    key={`tick-${tick}`}
                    className="absolute right-2"
                    style={{ bottom: `${tick * pxPerMin}px`, transform: 'translateY(50%)' }}
                  >
                    {tick}
                  </span>
                ))}
              </div>
            </div>
            {/* Columns */}
            <div className="flex gap-3">
              {columns
                // Only hide the unassigned bucket when empty; all role columns always render
                .filter((column) => column.id !== UNASSIGNED_ROLE_ID || (groupedCards[column.id]?.length ?? 0) > 0)
                .map((column) => (
                  <DroppableColumn
                    key={column.id}
                    column={column}
                    cards={groupedCards[column.id] ?? []}
                    totalLoad={simulatedTotalsByColumn[column.id] ?? 0}
                    capacity={capacitiesByColumn[column.id] ?? shiftMinutes}
                    fteCount={Math.max(1, Number(fteByRole[column.id] ?? 1))}
                    pxPerMin={pxPerMin}
                    canvasHeight={canvasHeight}
                    scrollTop={scrollTop}
                    viewportPxHeight={plotHeight}
                    taktMinutes={taktMinutes ?? undefined}
                    activeDragCardId={activeDragCardId}
                    viewMode={viewMode}
                    realLoad={column.roleId ? (realLoadByRole[column.roleId] ?? null) : null}
                    isSimulated={isSimulating}
                    stableLoad={totalsByColumn[column.id] ?? 0}
                    isDragging={activeDragCardId !== null}
                  />
                ))}
            </div>
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDragCard ? (
            <div
              className="w-[240px] rounded-md border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2.5 py-1.5 shadow-[0_4px_16px_rgba(0,0,0,0.18)] pointer-events-none flex flex-col justify-between transition-all duration-75"
              style={{
                height: `${activeDragCardHeight}px`,
                opacity: isDragOverValid ? 1 : 0.5,
                cursor: isDragOverValid ? 'grabbing' : 'not-allowed',
                ring: isDragOverValid ? '2px solid var(--brand-primary)' : undefined,
                outline: isDragOverValid ? '2px solid var(--brand-primary)' : '2px solid rgba(239,68,68,0.5)',
                outlineOffset: '1px',
              }}
            >
              <p className="truncate text-[11px] font-semibold leading-tight text-[var(--text-primary)]">
                {activeDragCard.atividade || 'Atividade sem nome'}
              </p>
              <div className="mt-1 flex items-center justify-between gap-2 text-[10px] leading-none text-[var(--text-secondary)]">
                <span className="font-mono">{Math.round(getCycleTimeMinutes(activeDragCard))} min</span>
                <span className="truncate text-right">
                  {columns.find((c) => c.id === (activeAssignments[activeDragCard.id] ?? UNASSIGNED_ROLE_ID))?.name ?? UNASSIGNED_ROLE_LABEL}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>


    </div>
  );
};

export default YamazumiSimulator;
